import { GraphQLClient } from 'graphql-request';

const GRAPHQL_ENDPOINT = `${window.location.origin}/graphql`;

export const getGraphQLClient = () => {
  const token = localStorage.getItem('token');

  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  return new GraphQLClient(GRAPHQL_ENDPOINT, {
    headers,
  });
};

// Download helpers
export const downloadBookAsEpub = async (bookId: string, bookTitle: string) => {
  const token = localStorage.getItem('token');
  const url = `${window.location.origin}/books/${bookId}/download/epub`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to download EPUB');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `${bookTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.epub`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

// Cover image helpers
export const uploadBookCover = async (
  bookId: string,
  file: File
): Promise<{ coverImageUrl?: string; coverImageOriginalUrl?: string; errors?: string[] }> => {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('cover_image', file);

  const response = await fetch(`${window.location.origin}/books/${bookId}/cover`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  return response.json();
};

export const removeBookCover = async (
  bookId: string
): Promise<{ success?: boolean; errors?: string[] }> => {
  const token = localStorage.getItem('token');

  const response = await fetch(`${window.location.origin}/books/${bookId}/cover`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.json();
};

// Auth mutations
export const SIGN_UP = `
  mutation SignUp($email: String!, $password: String!) {
    signUp(input: { email: $email, password: $password }) {
      token
      user {
        id
        email
        name
        language
        admin
        profileComplete
      }
      errors
    }
  }
`;

export const SIGN_IN = `
  mutation SignIn($email: String!, $password: String!) {
    signIn(input: { email: $email, password: $password }) {
      token
      user {
        id
        email
        name
        language
        admin
      }
      errors
    }
  }
`;

export const GOOGLE_SIGN_IN = `
  mutation GoogleSignIn($idToken: String!) {
    googleSignIn(input: { idToken: $idToken }) {
      token
      user {
        id
        email
        name
        language
        admin
      }
      errors
    }
  }
`;

export const REQUEST_PASSWORD_RESET = `
  mutation RequestPasswordReset($email: String!) {
    requestPasswordReset(input: { email: $email }) {
      success
      errors
    }
  }
`;

export const RESET_PASSWORD = `
  mutation ResetPassword($token: String!, $password: String!) {
    resetPassword(input: { token: $token, password: $password }) {
      success
      errors
    }
  }
`;

export const CONFIRM_EMAIL = `
  mutation ConfirmEmail($token: String!) {
    confirmEmail(input: { token: $token }) {
      success
      token
      user {
        id
        email
        name
        language
        admin
      }
      errors
    }
  }
`;

export const RESEND_CONFIRMATION_EMAIL = `
  mutation ResendConfirmationEmail($email: String!) {
    resendConfirmationEmail(input: { email: $email }) {
      success
      errors
    }
  }
`;

// User queries
export const GET_ME = `
  query GetMe {
    me {
      id
      email
      name
      language
      admin
      booksCount
      birthday
      gender
      biography
      occupation
      interests
      expertiseAreas
      preferredWritingStyle
      preferredAiModel
      profileComplete
      lastLocation {
        path
        kind
        updatedAt
      }
    }
  }
`;

export const RECORD_LAST_LOCATION = `
  mutation RecordLastLocation($path: String!, $kind: String!) {
    recordLastLocation(input: { path: $path, kind: $kind }) {
      user {
        id
        lastLocation {
          path
          kind
          updatedAt
        }
      }
      errors
    }
  }
`;

export const UPDATE_USER_LANGUAGE = `
  mutation UpdateUserLanguage($language: String!) {
    updateUserLanguage(input: { language: $language }) {
      user {
        id
        language
      }
      errors
    }
  }
`;

export const UPDATE_USER_PROFILE = `
  mutation UpdateUserProfile(
    $name: String
    $birthday: ISO8601Date
    $gender: String
    $biography: String
    $occupation: String
    $interests: [String!]
    $expertiseAreas: [String!]
    $preferredWritingStyle: String
    $preferredAiModel: String
  ) {
    updateUserProfile(input: {
      name: $name
      birthday: $birthday
      gender: $gender
      biography: $biography
      occupation: $occupation
      interests: $interests
      expertiseAreas: $expertiseAreas
      preferredWritingStyle: $preferredWritingStyle
      preferredAiModel: $preferredAiModel
    }) {
      user {
        id
        name
        birthday
        gender
        biography
        occupation
        interests
        expertiseAreas
        preferredWritingStyle
        preferredAiModel
        profileComplete
      }
      errors
    }
  }
`;

export const GENERATE_MCP_TOKEN = `
  mutation GenerateMcpToken($expiresInDays: Int) {
    generateMcpToken(input: { expiresInDays: $expiresInDays }) {
      token
      expiresAt
      errors
    }
  }
`;

// Book queries and mutations
export const GET_BOOKS = `
  query GetBooks {
    books {
      id
      title
      fullTitle
      bookType
      themes
      language
      summary
      status
      published
      publishedAt
      plotBeatsCount
      wordCount
      coverImageUrl
      createdAt
      updatedAt
    }
  }
`;

export const GET_DRAFT_BOOKS = `
  query GetDraftBooks {
    draftBooks {
      id
      title
      fullTitle
      bookType
      themes
      language
      summary
      status
      coverImageUrl
      createdAt
      updatedAt
    }
  }
`;

export const GET_PUBLISHED_BOOKS = `
  query GetPublishedBooks($limit: Int, $offset: Int) {
    publishedBooks(limit: $limit, offset: $offset) {
      id
      title
      fullTitle
      bookType
      themes
      language
      summary
      status
      published
      publishedAt
      plotBeatsCount
      wordCount
      viewsCount
      favoritesCount
      isFavorited
      coverImageUrl
      user {
        id
        name
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_TRENDING_BOOKS = `
  query GetTrendingBooks($limit: Int) {
    trendingBooks(limit: $limit) {
      id
      title
      fullTitle
      bookType
      themes
      language
      summary
      status
      published
      publishedAt
      plotBeatsCount
      wordCount
      viewsCount
      favoritesCount
      isFavorited
      coverImageUrl
      user {
        id
        name
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_MY_FAVORITE_BOOKS = `
  query GetMyFavoriteBooks {
    myFavoriteBooks {
      id
      title
      fullTitle
      bookType
      themes
      language
      summary
      status
      published
      publishedAt
      plotBeatsCount
      wordCount
      viewsCount
      favoritesCount
      isFavorited
      coverImageUrl
      user {
        id
        name
      }
      createdAt
      updatedAt
    }
  }
`;

export const TOGGLE_FAVORITE = `
  mutation ToggleFavorite($bookId: ID!) {
    toggleFavorite(input: { bookId: $bookId }) {
      book {
        id
        favoritesCount
        isFavorited
      }
      favorited
      errors
    }
  }
`;

export const RECORD_BOOK_VIEW = `
  mutation RecordBookView($bookId: ID!) {
    recordBookView(input: { bookId: $bookId }) {
      book {
        id
        viewsCount
      }
      errors
    }
  }
`;

export const GET_BOOK_HEADER = `
  query GetBookHeader($id: ID!) {
    book(id: $id) {
      id
      title
      structureLocked
      chapters {
        id
        title
        position
        scenes {
          id
          title
          position
          planPoints {
            id
            content
            position
          }
        }
      }
    }
  }
`;

export const GET_BOOK = `
  query GetBook($id: ID!) {
    book(id: $id) {
      id
      title
      subtitle
      fullTitle
      bookType
      themes
      language
      summary
      status
      wizardStep
      contentFillMode
      published
      publishedAt
      plotBeatsCount
      wordCount
      coverImageUrl
      coverImageOriginalUrl
      coverSettings
      frontMatterSettings
      readerAgeFrom
      readerAgeTo
      readerAgeDescription
      audienceReviewed
      additionalInstructions
      writingStyle
      storyDirection
      plotBeats {
        id
        title
        description
        status
      }
      expansionStatus
      expansionStartedAt
      expansionCompletedAt
      allBeatsCompleted
      readyForExpansion
      storyBibleCharacters {
        id
        name
        description
        role
        goal
        flaw
        arc
        isStoryteller
        locked
        createdAt
        updatedAt
      }
      storyBibleSettings {
        id
        name
        title
        description
        timePeriod
        locations
        worldRules
        atmosphere
        locked
        createdAt
        updatedAt
      }
      storyBibleThemes {
        id
        centralTheme
        secondaryThemes
        description
        locked
        createdAt
        updatedAt
      }
      storyBibleRelationships {
        id
        name
        description
        characters
        dynamic
        evolution
        locked
        createdAt
        updatedAt
      }
      storyBiblePlotBeats {
        id
        title
        description
        summary
        locked
        draftBlock {
          id
          content
          extendedTextBlock {
            id
            content
            wordCount
            factsGatheringFinishedAt
            finishedAt
            factsNeedRecheck
            updatedAt
          }
          upstreamChangedAt
          contentMayBeOutdated
          status
          wordCount
          enrichmentStatus
          purpose
          readyForEnrichment
          readyForEarlyProse
          earlyProseContent
          earlyProseApproved
          earlyProseApprovedAt
          lastGenerationMode
          interviewStatus
          hasInterviews
          interviewsCount
          deepDivePhase
          deepDiveApproved
          deepDiveApprovedAt
          interviewUserWordCount
          interviewQuestionsAnswered
          interviewReady
          interviewCoveragePct
          interviewNeedsReevaluation
          interviewEvaluation
          interviewExtractedBlocks
          mainIdeas
          writingStyle
          writingStyleSuggestions
          proseClaimMappings {
            sentenceIndex
            sentenceText
            claimIds
            claimTypes
          }
          activeGenerateBeatFactsJob {
            id
            status
            progress
            createdAt
          }
          activeGenerateBeatEnrichmentJob {
            id
            status
            progress
            createdAt
          }
          activeGenerateEarlyProseJob {
            id
            status
            progress
            createdAt
          }
          activeGenerateWritingStyleJob {
            id
            status
            progress
            createdAt
          }
          coreClaims {
            id
            content
            position
            sourceType
            locked
            draftBlockId
            bookId
            beatNumber
            createdAt
            updatedAt
          }
          contentExclusions {
            id
            content
            position
            locked
            draftBlockId
            bookId
            beatNumber
            createdAt
            updatedAt
          }
          beatFragments {
            id
            content
            position
            draftBlockId
            bookId
            createdAt
            updatedAt
          }
          coreClaimIds
          contentExclusionIds
          claimsNeedRecheck
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      workflowStepStatuses
      currentWorkflowStep
      additionalDetails {
        id
        stepType
        freeFormText
        entries {
          id
          text
          position
        }
        questions {
          id
          questionText
          aiSuggestions
          selectedAnswer
          skipped
          position
        }
        compiledContext
        hasAnyContent
      }
      bookExclusions {
        id
        content
        position
        bookId
        createdAt
        updatedAt
      }
      user {
        id
        name
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_THEMES_FOR_BOOK_TYPE = `
  query GetThemesForBookType($bookType: BookTypeEnum!) {
    themesForBookType(bookType: $bookType)
  }
`;

export const CREATE_BOOK = `
  mutation CreateBook($title: String, $bookType: BookTypeEnum, $themes: [String!], $language: String, $summary: String, $readerAgeFrom: Int, $readerAgeTo: Int, $readerGender: String, $additionalInstructions: String) {
    createBook(input: { title: $title, bookType: $bookType, themes: $themes, language: $language, summary: $summary, readerAgeFrom: $readerAgeFrom, readerAgeTo: $readerAgeTo, readerGender: $readerGender, additionalInstructions: $additionalInstructions }) {
      book {
        id
        title
        bookType
        themes
        language
        summary
        status
        wizardStep
      }
      errors
    }
  }
`;

export const UPDATE_BOOK = `
  mutation UpdateBook($id: ID!, $title: String, $subtitle: String, $bookType: BookTypeEnum, $themes: [String!], $language: String, $summary: String, $status: BookStatusEnum, $wizardStep: WizardStepEnum, $lastContentPageCount: Int, $contentFillMode: ContentFillModeEnum, $readerAgeFrom: Int, $readerAgeTo: Int, $audienceReviewed: Boolean, $additionalInstructions: String, $writingStyle: String, $frontMatterSettings: JSON, $plotBeats: [PlotBeatInput!]) {
    updateBook(input: { id: $id, title: $title, subtitle: $subtitle, bookType: $bookType, themes: $themes, language: $language, summary: $summary, status: $status, wizardStep: $wizardStep, lastContentPageCount: $lastContentPageCount, contentFillMode: $contentFillMode, readerAgeFrom: $readerAgeFrom, readerAgeTo: $readerAgeTo, audienceReviewed: $audienceReviewed, additionalInstructions: $additionalInstructions, writingStyle: $writingStyle, frontMatterSettings: $frontMatterSettings, plotBeats: $plotBeats }) {
      book {
        id
        title
        subtitle
        fullTitle
        bookType
        themes
        language
        summary
        status
        wizardStep
        lastContentPageCount
        contentFillMode
        readerAgeFrom
        readerAgeTo
        readerAgeDescription
        audienceReviewed
        additionalInstructions
        writingStyle
        frontMatterSettings
        plotBeats {
          id
          title
          description
          status
        }
      }
      errors
    }
  }
`;

export const DELETE_BOOK = `
  mutation DeleteBook($id: ID!) {
    deleteBook(input: { id: $id }) {
      success
      errors
    }
  }
`;

export const PUBLISH_BOOK = `
  mutation PublishBook($id: ID!, $published: Boolean!) {
    publishBook(input: { id: $id, published: $published }) {
      book {
        id
        published
        publishedAt
      }
      errors
    }
  }
`;

// AI generation mutations
export const GENERATE_RANDOM_TITLE = `
  mutation GenerateRandomTitle($bookType: BookTypeEnum!, $themes: [String!]!, $language: String, $count: Int, $summary: String, $currentTitle: String, $readerAgeFrom: Int, $readerAgeTo: Int) {
    generateRandomTitle(input: { bookType: $bookType, themes: $themes, language: $language, count: $count, summary: $summary, currentTitle: $currentTitle, readerAgeFrom: $readerAgeFrom, readerAgeTo: $readerAgeTo }) {
      titles
      errors
    }
  }
`;

export const GENERATE_SUBTITLE = `
  mutation GenerateSubtitle($title: String!, $themes: [String!], $summary: String, $language: String, $count: Int) {
    generateSubtitle(input: { title: $title, themes: $themes, summary: $summary, language: $language, count: $count }) {
      subtitles
      errors
    }
  }
`;

export const GENERATE_RANDOM_SUMMARY = `
  mutation GenerateRandomSummary($bookType: BookTypeEnum!, $themes: [String!]!, $title: String, $language: String) {
    generateRandomSummary(input: { bookType: $bookType, themes: $themes, title: $title, language: $language }) {
      summary
      errors
    }
  }
`;

export const GENERATE_SUMMARY_OPTIONS = `
  mutation GenerateSummaryOptions($bookType: BookTypeEnum!, $themes: [String!]!, $title: String, $language: String, $summary: String, $readerAgeFrom: Int, $readerAgeTo: Int) {
    generateSummaryOptions(input: { bookType: $bookType, themes: $themes, title: $title, language: $language, summary: $summary, readerAgeFrom: $readerAgeFrom, readerAgeTo: $readerAgeTo }) {
      summaries {
        id
        summary
      }
      errors
    }
  }
`;

export const GENERATE_STORYTELLER_OPTIONS = `
  mutation GenerateStorytellerOptions($bookId: ID!) {
    generateStorytellerOptions(input: { bookId: $bookId }) {
      options {
        id
        title
        content
      }
      errors
    }
  }
`;

export const GENERATE_WRITING_STYLE_OPTIONS = `
  mutation GenerateWritingStyleOptions($bookId: ID!) {
    generateWritingStyleOptions(input: { bookId: $bookId }) {
      options {
        id
        title
        content
      }
      errors
    }
  }
`;

export const GENERATE_COVER_IMAGE = `
  mutation GenerateCoverImage($bookId: ID!, $customInstructions: String) {
    generateCoverImage(input: { bookId: $bookId, customInstructions: $customInstructions }) {
      coverImageUrl
      errors
    }
  }
`;

export const GENERATE_BEAT_FIELD = `
  mutation GenerateBeatField($bookId: ID!, $field: String!, $currentTitle: String, $currentPurpose: String, $currentWhatUserLearns: String, $beatId: ID, $mode: String, $count: Int) {
    generateBeatField(input: { bookId: $bookId, field: $field, currentTitle: $currentTitle, currentPurpose: $currentPurpose, currentWhatUserLearns: $currentWhatUserLearns, beatId: $beatId, mode: $mode, count: $count }) {
      value
      values
      errors
    }
  }
`;

export const GENERATE_BEAT = `
  mutation GenerateBeat($bookId: ID!, $currentTitle: String, $currentDescription: String, $beatId: ID, $count: Int) {
    generateBeat(input: { bookId: $bookId, currentTitle: $currentTitle, currentDescription: $currentDescription, beatId: $beatId, count: $count }) {
      title
      description
      whatUserLearns
      suggestions {
        title
        description
      }
      errors
    }
  }
`;

export const GENERATE_STORY_BIBLE_ITEM = `
  mutation GenerateStoryBibleItem(
    $bookId: ID!
    $itemType: String!
    $currentName: String
    $currentRole: String
    $currentGoal: String
    $currentFlaw: String
    $currentArc: String
    $currentTimePeriod: String
    $currentLocations: [String!]
    $currentWorldRules: String
    $currentAtmosphere: String
    $currentCentralTheme: String
    $currentSecondaryThemes: [String!]
    $currentTitle: String
    $currentCharacters: [String!]
    $currentDynamic: String
    $currentEvolution: String
    $currentDescription: String
  ) {
    generateStoryBibleItem(input: {
      bookId: $bookId
      itemType: $itemType
      currentName: $currentName
      currentRole: $currentRole
      currentGoal: $currentGoal
      currentFlaw: $currentFlaw
      currentArc: $currentArc
      currentTimePeriod: $currentTimePeriod
      currentLocations: $currentLocations
      currentWorldRules: $currentWorldRules
      currentAtmosphere: $currentAtmosphere
      currentCentralTheme: $currentCentralTheme
      currentSecondaryThemes: $currentSecondaryThemes
      currentTitle: $currentTitle
      currentCharacters: $currentCharacters
      currentDynamic: $currentDynamic
      currentEvolution: $currentEvolution
      currentDescription: $currentDescription
    }) {
      name
      role
      goal
      flaw
      arc
      timePeriod
      locations
      worldRules
      atmosphere
      centralTheme
      secondaryThemes
      title
      characters
      dynamic
      evolution
      description
      errors
    }
  }
`;

export const GENERATE_STORY_BIBLE_ITEM_OPTIONS = `
  mutation GenerateStoryBibleItemOptions(
    $bookId: ID!
    $itemType: String!
    $currentName: String
    $currentRole: String
    $currentGoal: String
    $currentFlaw: String
    $currentArc: String
    $currentTimePeriod: String
    $currentLocations: [String!]
    $currentWorldRules: String
    $currentAtmosphere: String
    $currentCentralTheme: String
    $currentSecondaryThemes: [String!]
    $currentTitle: String
    $currentCharacters: [String!]
    $currentDynamic: String
    $currentEvolution: String
    $currentDescription: String
    $instructions: String
  ) {
    generateStoryBibleItemOptions(input: {
      bookId: $bookId
      itemType: $itemType
      currentName: $currentName
      currentRole: $currentRole
      currentGoal: $currentGoal
      currentFlaw: $currentFlaw
      currentArc: $currentArc
      currentTimePeriod: $currentTimePeriod
      currentLocations: $currentLocations
      currentWorldRules: $currentWorldRules
      currentAtmosphere: $currentAtmosphere
      currentCentralTheme: $currentCentralTheme
      currentSecondaryThemes: $currentSecondaryThemes
      currentTitle: $currentTitle
      currentCharacters: $currentCharacters
      currentDynamic: $currentDynamic
      currentEvolution: $currentEvolution
      currentDescription: $currentDescription
      instructions: $instructions
    }) {
      options {
        id
        itemType
        name
        role
        goal
        flaw
        arc
        timePeriod
        locations
        worldRules
        atmosphere
        centralTheme
        secondaryThemes
        title
        characters
        dynamic
        evolution
        description
      }
      errors
    }
  }
`;

// Background Jobs
export const GET_BACKGROUND_JOB = `
  query GetBackgroundJob($id: ID!) {
    backgroundJob(id: $id) {
      id
      jobType
      targetType
      targetId
      status
      progress
      errorMessage
      result
      partialResult
      metadata
      startedAt
      estimatedDurationMs
      createdAt
      updatedAt
    }
  }
`;

export const CANCEL_BACKGROUND_JOB = `
  mutation CancelBackgroundJob($jobId: ID!) {
    cancelBackgroundJob(input: { jobId: $jobId }) {
      backgroundJob {
        id
        status
      }
      success
      errors
    }
  }
`;

export const GET_ACTIVE_BACKGROUND_JOBS = `
  query GetActiveBackgroundJobs {
    activeBackgroundJobs {
      id
      jobType
      targetType
      targetId
      status
      progress
      errorMessage
      result
      metadata
      startedAt
      estimatedDurationMs
      createdAt
      updatedAt
      targetInfo {
        bookTitle
        beatTitle
        beatNumber
      }
    }
  }
`;

export const GET_BACKGROUND_JOBS_FOR_TARGET = `
  query GetBackgroundJobsForTarget($targetType: String!, $targetId: ID!) {
    backgroundJobsForTarget(targetType: $targetType, targetId: $targetId) {
      id
      jobType
      targetType
      targetId
      status
      progress
      errorMessage
      result
      metadata
      startedAt
      estimatedDurationMs
      createdAt
      updatedAt
      targetInfo {
        bookTitle
        beatTitle
        beatNumber
      }
    }
  }
`;

export const GENERATE_COVER_IMAGE_ASYNC = `
  mutation GenerateCoverImageAsync($bookId: ID!, $customInstructions: String) {
    generateCoverImage(input: { bookId: $bookId, async: true, customInstructions: $customInstructions }) {
      backgroundJob {
        id
        jobType
        targetType
        targetId
        status
        progress
        metadata
        startedAt
        estimatedDurationMs
        createdAt
        updatedAt
      }
      errors
    }
  }
`;

// Admin: Gemini Logs
export const GET_AI_PROMPT_LOGS = `
  query AiPromptLogs($bookId: ID, $userId: ID, $requestType: String, $modelUsed: String, $success: Boolean, $startDate: ISO8601Date, $endDate: ISO8601Date, $limit: Int, $offset: Int) {
    aiPromptLogs(bookId: $bookId, userId: $userId, requestType: $requestType, modelUsed: $modelUsed, success: $success, startDate: $startDate, endDate: $endDate, limit: $limit, offset: $offset) {
      id
      requestType
      bookId
      bookTitle
      userId
      userName
      modelUsed
      durationMs
      inputTokens
      outputTokens
      totalTokens
      estimatedCost
      outputWordCount
      errorMessage
      success
      createdAt
    }
    aiPromptLogsStats(startDate: $startDate, endDate: $endDate, bookId: $bookId, userId: $userId, requestType: $requestType) {
      total
      today
      failed
      avgDurationMs
      totalInputTokens
      totalOutputTokens
      totalTokens
      estimatedCost
      byType {
        requestType
        count
      }
    }
    aiPromptLogRequestTypes
    booksWithLogs {
      id
      title
    }
    usersWithLogs {
      id
      name
      email
    }
  }
`;

export const GET_AI_LOGS_GROUPED = `
  query AiLogsGrouped($startDate: ISO8601Date, $endDate: ISO8601Date) {
    aiLogsGroupedByUser(startDate: $startDate, endDate: $endDate) {
      groupId
      groupName
      groupType
      requestCount
      inputTokens
      outputTokens
      totalTokens
      avgDurationMs
      failedCount
      estimatedCost
    }
    aiLogsGroupedByType(startDate: $startDate, endDate: $endDate) {
      groupId
      groupName
      groupType
      requestCount
      inputTokens
      outputTokens
      totalTokens
      avgDurationMs
      failedCount
      estimatedCost
    }
    aiLogsGroupedByBook(startDate: $startDate, endDate: $endDate) {
      groupId
      groupName
      groupType
      requestCount
      inputTokens
      outputTokens
      totalTokens
      avgDurationMs
      failedCount
      estimatedCost
    }
  }
`;

export const GET_AI_PROMPT_LOG_DETAIL = `
  query AiPromptLogDetail($id: ID!) {
    aiPromptLog(id: $id) {
      id
      requestType
      bookId
      bookTitle
      userId
      userName
      userEmail
      inputPrompt
      outputResponse
      modelUsed
      durationMs
      inputTokens
      outputTokens
      totalTokens
      estimatedCost
      errorMessage
      success
      createdAt
    }
  }
`;

// Token Usage Stats
export const GET_TOKEN_USAGE_BY_USER = `
  query TokenUsageByUser($startDate: ISO8601Date, $endDate: ISO8601Date) {
    tokenUsageByUser(startDate: $startDate, endDate: $endDate) {
      userId
      userName
      userEmail
      totalInputTokens
      totalOutputTokens
      totalTokens
      estimatedCost
      requestCount
    }
  }
`;

export const GET_TOKEN_USAGE_BY_DAY = `
  query TokenUsageByDay($userId: ID, $startDate: ISO8601Date, $endDate: ISO8601Date) {
    tokenUsageByDay(userId: $userId, startDate: $startDate, endDate: $endDate) {
      date
      totalInputTokens
      totalOutputTokens
      totalTokens
      estimatedCost
      requestCount
    }
  }
`;

export const GET_TOKEN_USAGE_BY_REQUEST_TYPE = `
  query TokenUsageByRequestType($userId: ID, $startDate: ISO8601Date, $endDate: ISO8601Date) {
    tokenUsageByRequestType(userId: $userId, startDate: $startDate, endDate: $endDate) {
      requestType
      totalInputTokens
      totalOutputTokens
      totalTokens
      estimatedCost
      requestCount
    }
  }
`;

export const GET_MY_TOKEN_USAGE_TODAY = `
  query MyTokenUsageToday {
    myTokenUsageToday {
      date
      totalInputTokens
      totalOutputTokens
      totalTokens
      estimatedCost
      requestCount
    }
  }
`;

export const SAVE_PROCESSED_COVER = `
  mutation SaveProcessedCover($bookId: ID!, $imageData: String!, $coverSettings: JSON) {
    saveProcessedCover(input: { bookId: $bookId, imageData: $imageData, coverSettings: $coverSettings }) {
      coverImageUrl
      errors
    }
  }
`;

// Progressive Outlining Mutations
export const GENERATE_STORY_BIBLE = `
  mutation GenerateStoryBible($bookId: ID!, $beatCount: Int, $instructions: String) {
    generateStoryBible(input: { bookId: $bookId, beatCount: $beatCount, instructions: $instructions }) {
      book {
        id
        storyBible
        plotBeats {
          id
          title
        }
      }
      storyBible
      plotBeats {
        id
        title
        status
      }
      errors
    }
  }
`;

// Write Story (Narrative Block) Mutations
export const WRITE_NARRATIVE_BLOCK = `
  mutation WriteNarrativeBlock($bookId: ID!, $beatId: ID!) {
    writeNarrativeBlock(input: { bookId: $bookId, beatId: $beatId }) {
      success
      content
      draftBlock {
        id
        content
        status
        wordCount
      }
      beat {
        id
        title
        description
        status
      }
      nextBeatIndex
      errors
    }
  }
`;

export const APPROVE_NARRATIVE_BLOCK = `
  mutation ApproveNarrativeBlock($bookId: ID!, $beatId: ID!, $content: String!) {
    approveNarrativeBlock(input: { bookId: $bookId, beatId: $beatId, content: $content }) {
      success
      draftBlock {
        id
        content
        status
        wordCount
      }
      nextBeatIndex
      allBeatsCompleted
      errors
    }
  }
`;

export const EXPAND_BOOK = `
  mutation ExpandBook($bookId: ID!) {
    expandBook(input: { bookId: $bookId }) {
      success
      book {
        id
        expansionStatus
        expansionStartedAt
        expansionCompletedAt
        allBeatsCompleted
        readyForExpansion
      }
      backgroundJob {
        id
        jobType
        targetType
        targetId
        status
        progress
        metadata
        createdAt
        startedAt
        updatedAt
      }
      errors
    }
  }
`;

export const EXPAND_DRAFT_BLOCK = `
  mutation ExpandDraftBlock($bookId: ID!, $draftBlockId: ID!, $force: Boolean) {
    expandDraftBlock(input: { bookId: $bookId, draftBlockId: $draftBlockId, force: $force }) {
      success
      backgroundJob {
        id
        jobType
        targetType
        targetId
        status
        progress
        metadata
        createdAt
        startedAt
        updatedAt
      }
      errors
    }
  }
`;

export const APPROVE_ALL_PENDING_FACTS = `
  mutation ApproveAllPendingFacts($input: ApproveAllPendingFactsInput!) {
    approveAllPendingFacts(input: $input) {
      success
      approvedCount
      errors
    }
  }
`;

// Story Bible Item Mutations
export const STORY_BIBLE_ITEM_FIELDS = `
  fragment StoryBibleItemFields on StoryBibleItem {
    id
    itemType
    position
    locked
    title
    name
    description
    isStoryteller
    role
    goal
    flaw
    arc
    timePeriod
    locations
    worldRules
    atmosphere
    centralTheme
    secondaryThemes
    characters
    dynamic
    evolution
    draftBlock {
      id
      content
      extendedTextBlock {
        id
        content
        wordCount
        factsGatheringFinishedAt
        finishedAt
        factsNeedRecheck
        updatedAt
      }
      upstreamChangedAt
      contentMayBeOutdated
      status
      wordCount
      enrichmentStatus
      purpose
      readyForEnrichment
      coreClaims {
        id
        content
        position
        sourceType
        locked
        draftBlockId
        bookId
        beatNumber
        createdAt
        updatedAt
      }
      contentExclusions {
        id
        content
        position
        locked
        draftBlockId
        bookId
        beatNumber
        createdAt
        updatedAt
      }
      beatFragments {
        id
        content
        position
        draftBlockId
        bookId
        createdAt
        updatedAt
      }
      activeGenerateBeatFactsJob {
        id
        jobType
        status
        progress
        errorMessage
        createdAt
        updatedAt
      }
      activeGenerateBeatEnrichmentJob {
        id
        jobType
        status
        progress
        errorMessage
        createdAt
        updatedAt
      }
      activeGenerateEarlyProseJob {
        id
        jobType
        status
        progress
        errorMessage
        createdAt
        updatedAt
      }
      activeGenerateWritingStyleJob {
        id
        jobType
        status
        progress
        errorMessage
        createdAt
        updatedAt
      }
      earlyProseContent
      earlyProseApprovedAt
      readyForEarlyProse
      earlyProseApproved
      interviewStatus
      hasInterviews
      interviewsCount
      writingStyle
      writingStyleSuggestions
      proseClaimMappings {
        sentenceIndex
        sentenceText
        claimIds
        claimTypes
      }
      beatToneOverrides
      createdAt
      updatedAt
    }
    createdAt
    updatedAt
  }
`;

export const CREATE_STORY_BIBLE_ITEM = `
  ${STORY_BIBLE_ITEM_FIELDS}
  mutation CreateStoryBibleItem(
    $bookId: ID!
    $itemType: String!
    $position: Int
    $title: String
    $name: String
    $description: String
    $isStoryteller: Boolean
    $role: String
    $goal: String
    $flaw: String
    $arc: String
    $timePeriod: String
    $locations: [String!]
    $worldRules: String
    $atmosphere: String
    $centralTheme: String
    $secondaryThemes: [String!]
    $characters: [String!]
    $dynamic: String
    $evolution: String
  ) {
    createStoryBibleItem(input: {
      bookId: $bookId
      itemType: $itemType
      position: $position
      title: $title
      name: $name
      description: $description
      isStoryteller: $isStoryteller
      role: $role
      goal: $goal
      flaw: $flaw
      arc: $arc
      timePeriod: $timePeriod
      locations: $locations
      worldRules: $worldRules
      atmosphere: $atmosphere
      centralTheme: $centralTheme
      secondaryThemes: $secondaryThemes
      characters: $characters
      dynamic: $dynamic
      evolution: $evolution
    }) {
      storyBibleItem {
        ...StoryBibleItemFields
      }
      errors
    }
  }
`;

export const UPDATE_STORY_BIBLE_ITEM = `
  ${STORY_BIBLE_ITEM_FIELDS}
  mutation UpdateStoryBibleItem(
    $id: ID!
    $position: Int
    $locked: Boolean
    $title: String
    $name: String
    $description: String
    $isStoryteller: Boolean
    $role: String
    $goal: String
    $flaw: String
    $arc: String
    $timePeriod: String
    $locations: [String!]
    $worldRules: String
    $atmosphere: String
    $centralTheme: String
    $secondaryThemes: [String!]
    $characters: [String!]
    $dynamic: String
    $evolution: String
  ) {
    updateStoryBibleItem(input: {
      id: $id
      position: $position
      locked: $locked
      title: $title
      name: $name
      description: $description
      isStoryteller: $isStoryteller
      role: $role
      goal: $goal
      flaw: $flaw
      arc: $arc
      timePeriod: $timePeriod
      locations: $locations
      worldRules: $worldRules
      atmosphere: $atmosphere
      centralTheme: $centralTheme
      secondaryThemes: $secondaryThemes
      characters: $characters
      dynamic: $dynamic
      evolution: $evolution
    }) {
      storyBibleItem {
        ...StoryBibleItemFields
      }
      errors
    }
  }
`;

export const DELETE_STORY_BIBLE_ITEM = `
  mutation DeleteStoryBibleItem($id: ID!) {
    deleteStoryBibleItem(input: { id: $id }) {
      success
      errors
    }
  }
`;

export const REORDER_STORY_BIBLE_ITEMS = `
  ${STORY_BIBLE_ITEM_FIELDS}
  mutation ReorderStoryBibleItems($bookId: ID!, $itemType: String!, $itemIds: [ID!]!) {
    reorderStoryBibleItems(input: { bookId: $bookId, itemType: $itemType, itemIds: $itemIds }) {
      storyBibleItems {
        ...StoryBibleItemFields
      }
      errors
    }
  }
`;

// Error Logging
export const LOG_JS_ERROR = `
  mutation LogJsError($errorClass: String!, $message: String!, $stack: String, $url: String, $userAgent: String, $context: JSON) {
    logJsError(input: { errorClass: $errorClass, message: $message, stack: $stack, url: $url, userAgent: $userAgent, context: $context }) {
      success
    }
  }
`;

// Beat Facts and Enrichment Mutations
export const GENERATE_ALL_FACTS = `
  mutation GenerateAllFacts($bookId: ID!) {
    generateAllFacts(input: { bookId: $bookId }) {
      success
      backgroundJob {
        id
        jobType
        targetType
        targetId
        status
        progress
        metadata
        createdAt
        updatedAt
      }
      beatsToProcess
      errors
    }
  }
`;

export const GENERATE_CORE_CLAIMS = `
  mutation GenerateCoreClaims($input: GenerateCoreClaimsInput!) {
    generateCoreClaims(input: $input) {
      success
      claims {
        id
        content
        position
        sourceType
        locked
        draftBlockId
        bookId
        beatNumber
        createdAt
        updatedAt
      }
      errors
    }
  }
`;

export const SAVE_CORE_CLAIM = `
  mutation SaveCoreClaim($bookId: ID!, $content: String!, $draftBlockId: ID!) {
    saveCoreClaim(input: { bookId: $bookId, content: $content, draftBlockId: $draftBlockId }) {
      claim {
        id
        content
        position
        sourceType
      }
      errors
    }
  }
`;

export const GENERATE_SINGLE_FACT = `
  mutation GenerateSingleFact($draftBlockId: ID!, $guidance: String) {
    generateSingleFact(input: { draftBlockId: $draftBlockId, guidance: $guidance }) {
      suggestion
      success
      errors
    }
  }
`;

export const FINALIZE_BEAT_FACTS = `
  mutation FinalizeBeatFacts($draftBlockId: ID!) {
    finalizeBeatFacts(input: { draftBlockId: $draftBlockId }) {
      success
      errors
      draftBlock {
        id
        enrichmentStatus
        readyForEnrichment
      }
    }
  }
`;

export const GENERATE_BEAT_ENRICHMENT = `
  mutation GenerateBeatEnrichment($draftBlockId: ID!) {
    generateBeatEnrichment(input: { draftBlockId: $draftBlockId }) {
      success
      errors
      backgroundJob {
        id
        jobType
        targetType
        targetId
        status
        progress
        errorMessage
        createdAt
        updatedAt
      }
      draftBlock {
        id
        enrichmentStatus
        purpose
        activeGenerateBeatEnrichmentJob {
          id
          status
          progress
        }
      }
    }
  }
`;

export const UPDATE_WHAT_USER_LEARNS = `
  mutation UpdateWhatUserLearns($draftBlockId: ID!, $whatUserLearns: String!) {
    updateWhatUserLearns(input: { draftBlockId: $draftBlockId, whatUserLearns: $whatUserLearns }) {
      success
      errors
      draftBlock {
        id
        purpose
      }
    }
  }
`;

export const GENERATE_WHAT_USER_LEARNS_ALTERNATIVES = `
  mutation GenerateWhatUserLearnsAlternatives($draftBlockId: ID!) {
    generateWhatUserLearnsAlternatives(input: { draftBlockId: $draftBlockId }) {
      success
      errors
      alternatives
    }
  }
`;

export const REGENERATE_WHAT_USER_LEARNS = `
  mutation RegenerateWhatUserLearns($draftBlockId: ID!) {
    regenerateWhatUserLearns(input: { draftBlockId: $draftBlockId }) {
      success
      errors
      whatUserLearns
    }
  }
`;

export const GENERATE_EARLY_PROSE = `
  mutation GenerateEarlyProse($draftBlockId: ID!, $mode: String) {
    generateEarlyProse(input: { draftBlockId: $draftBlockId, mode: $mode }) {
      success
      errors
      backgroundJob {
        id
        jobType
        status
        progress
        errorMessage
        createdAt
        updatedAt
      }
      draftBlock {
        id
        earlyProseContent
        earlyProseApprovedAt
        earlyProseApproved
      }
    }
  }
`;

export const APPROVE_EARLY_PROSE = `
  mutation ApproveEarlyProse($draftBlockId: ID!) {
    approveEarlyProse(input: { draftBlockId: $draftBlockId }) {
      success
      errors
      draftBlock {
        id
        earlyProseApprovedAt
        earlyProseApproved
      }
    }
  }
`;

export const UNAPPROVE_EARLY_PROSE = `
  mutation UnapproveEarlyProse($draftBlockId: ID!) {
    unapproveEarlyProse(input: { draftBlockId: $draftBlockId }) {
      success
      errors
      draftBlock {
        id
        earlyProseApprovedAt
        earlyProseApproved
      }
    }
  }
`;

export const APPROVE_DEEP_DIVE = `
  mutation ApproveDeepDive($draftBlockId: ID!) {
    approveDeepDive(input: { draftBlockId: $draftBlockId }) {
      success
      errors
      draftBlock {
        id
        deepDiveApproved
        deepDiveApprovedAt
      }
    }
  }
`;

export const UNAPPROVE_DEEP_DIVE = `
  mutation UnapproveDeepDive($draftBlockId: ID!) {
    unapproveDeepDive(input: { draftBlockId: $draftBlockId }) {
      success
      errors
      draftBlock {
        id
        deepDiveApproved
        deepDiveApprovedAt
      }
    }
  }
`;

export const GENERATE_WRITING_STYLE = `
  mutation GenerateWritingStyle($draftBlockId: ID!, $suggestion: String, $autoRegeneratePreview: Boolean) {
    generateWritingStyle(input: { draftBlockId: $draftBlockId, suggestion: $suggestion, autoRegeneratePreview: $autoRegeneratePreview }) {
      success
      errors
      backgroundJob {
        id
        jobType
        status
        progress
        errorMessage
        createdAt
        updatedAt
      }
      draftBlock {
        id
        writingStyle
        writingStyleSuggestions
      }
    }
  }
`;

export const UPDATE_WRITING_STYLE = `
  mutation UpdateWritingStyle($draftBlockId: ID!, $writingStyle: String!, $regeneratePreview: Boolean) {
    updateWritingStyle(input: { draftBlockId: $draftBlockId, writingStyle: $writingStyle, regeneratePreview: $regeneratePreview }) {
      success
      errors
      draftBlock {
        id
        writingStyle
      }
      previewBackgroundJob {
        id
        jobType
        status
        progress
        createdAt
      }
    }
  }
`;

// Claim locking mutations
export const LOCK_CORE_CLAIM = `
  mutation LockCoreClaim($claimId: ID!) {
    lockCoreClaim(input: { claimId: $claimId }) {
      success
      errors
      coreClaim {
        id
        locked
      }
    }
  }
`;

export const UNLOCK_CORE_CLAIM = `
  mutation UnlockCoreClaim($claimId: ID!) {
    unlockCoreClaim(input: { claimId: $claimId }) {
      success
      errors
      coreClaim {
        id
        locked
      }
    }
  }
`;

export const LOCK_ALL_CLAIMS = `
  mutation LockAllClaims($draftBlockId: ID!) {
    lockAllClaims(input: { draftBlockId: $draftBlockId }) {
      success
      errors
      lockedCount
    }
  }
`;

export const UNLOCK_ALL_CLAIMS = `
  mutation UnlockAllClaims($draftBlockId: ID!) {
    unlockAllClaims(input: { draftBlockId: $draftBlockId }) {
      success
      errors
      unlockedCount
    }
  }
`;

export const LOCK_CONTENT_EXCLUSION = `
  mutation LockContentExclusion($exclusionId: ID!) {
    lockContentExclusion(input: { exclusionId: $exclusionId }) {
      success
      errors
      contentExclusion {
        id
        locked
      }
    }
  }
`;

export const UNLOCK_CONTENT_EXCLUSION = `
  mutation UnlockContentExclusion($exclusionId: ID!) {
    unlockContentExclusion(input: { exclusionId: $exclusionId }) {
      success
      errors
      contentExclusion {
        id
        locked
      }
    }
  }
`;

// Book-level Exclusions
export const SAVE_BOOK_EXCLUSION = `
  mutation SaveBookExclusion($input: SaveBookExclusionInput!) {
    saveBookExclusion(input: $input) {
      exclusion {
        id
        content
        position
        bookId
        createdAt
        updatedAt
      }
      errors
    }
  }
`;

export const DELETE_BOOK_EXCLUSION = `
  mutation DeleteBookExclusion($input: DeleteBookExclusionInput!) {
    deleteBookExclusion(input: $input) {
      success
      errors
    }
  }
`;

// Instruction Suggestions
export const GENERATE_INSTRUCTION_SUGGESTIONS = `
  mutation GenerateInstructionSuggestions($bookId: ID!, $field: String, $count: Int) {
    generateInstructionSuggestions(input: { bookId: $bookId, field: $field, count: $count }) {
      suggestions
      errors
    }
  }
`;

// AI Suggestions for claims and exclusions
export const GENERATE_CLAIM_SUGGESTIONS = `
  mutation GenerateClaimSuggestions($draftBlockId: ID!, $guidance: String, $focusTag: String) {
    generateClaimSuggestions(input: { draftBlockId: $draftBlockId, guidance: $guidance, focusTag: $focusTag }) {
      suggestions {
        items {
          content
          rationale
          tag
        }
      }
      errors
    }
  }
`;

export const GENERATE_EXCLUSION_SUGGESTIONS = `
  mutation GenerateExclusionSuggestions($draftBlockId: ID!, $guidance: String, $focusCategory: String) {
    generateExclusionSuggestions(input: { draftBlockId: $draftBlockId, guidance: $guidance, focusCategory: $focusCategory }) {
      suggestions {
        commonPitfalls {
          content
          rationale
        }
        keepFocus {
          content
          rationale
        }
      }
      errors
    }
  }
`;

// Fragment Suggestions
export const GENERATE_FRAGMENT_SUGGESTIONS = `
  mutation GenerateFragmentSuggestions($draftBlockId: ID!, $focusTag: String) {
    generateFragmentSuggestions(input: { draftBlockId: $draftBlockId, focusTag: $focusTag }) {
      suggestions {
        items {
          content
          rationale
          tag
        }
      }
      errors
    }
  }
`;

// Beat Interview
export const GET_BEAT_INTERVIEWS = `
  query GetBeatInterviews($draftBlockId: ID!) {
    beatInterviews(draftBlockId: $draftBlockId) {
      id
      pageContext
      createdAt
      messages {
        id
        role
        content
        createdAt
      }
    }
  }
`;

export const SUGGEST_INTERVIEW_QUESTIONS = `
  query SuggestInterviewQuestions($draftBlockId: ID!) {
    suggestInterviewQuestions(draftBlockId: $draftBlockId)
  }
`;

export const START_BEAT_INTERVIEW = `
  mutation StartBeatInterview($draftBlockId: ID!, $firstQuestion: String) {
    startBeatInterview(input: { draftBlockId: $draftBlockId, firstQuestion: $firstQuestion }) {
      success
      errors
      message {
        id
        role
        content
        createdAt
      }
      chat {
        id
        pageContext
        createdAt
        messages {
          id
          role
          content
          createdAt
        }
      }
    }
  }
`;

export const SEND_INTERVIEW_MESSAGE = `
  mutation SendInterviewMessage($chatId: ID!, $content: String!, $evaluate: Boolean) {
    sendInterviewMessage(input: { chatId: $chatId, content: $content, evaluate: $evaluate }) {
      success
      errors
      interviewReady
      message {
        id
        role
        content
        createdAt
      }
      evaluation {
        areasCovered
        areasPartial
        areasMissing
        contentTypesFound
        contentTypesMissing
        coveragePct
        feedback
      }
    }
  }
`;

export const UPDATE_INTERVIEW_MESSAGE = `
  mutation UpdateInterviewMessage($messageId: ID!, $content: String!) {
    updateInterviewMessage(input: { messageId: $messageId, content: $content }) {
      success
      errors
      message {
        id
        role
        content
        createdAt
      }
    }
  }
`;

export const REGENERATE_INTERVIEW_QUESTION = `
  mutation RegenerateInterviewQuestion($messageId: ID!, $direction: String, $topic: String) {
    regenerateInterviewQuestion(input: { messageId: $messageId, direction: $direction, topic: $topic }) {
      success
      errors
      message {
        id
        role
        content
        createdAt
      }
    }
  }
`;

export const DELETE_BEAT_INTERVIEW = `
  mutation DeleteBeatInterview($chatId: ID!) {
    clearBeatInterview(input: { chatId: $chatId }) {
      success
      errors
    }
  }
`;

export const GET_ALTERNATIVE_INTERVIEW_QUESTIONS = `
  query GetAlternativeInterviewQuestions($messageId: ID!, $direction: String) {
    alternativeInterviewQuestions(messageId: $messageId, direction: $direction) {
      direction
      question
    }
  }
`;

export const REPLACE_INTERVIEW_QUESTION = `
  mutation ReplaceInterviewQuestion($messageId: ID!, $newQuestion: String!) {
    replaceInterviewQuestion(input: { messageId: $messageId, newQuestion: $newQuestion }) {
      success
      errors
      message {
        id
        role
        content
        createdAt
      }
    }
  }
`;

export const REEVALUATE_INTERVIEW = `
  mutation ReevaluateInterview($chatId: ID, $draftBlockId: ID) {
    reevaluateInterview(input: { chatId: $chatId, draftBlockId: $draftBlockId }) {
      success
      errors
      interviewReady
      evaluation {
        areasCovered
        areasPartial
        areasMissing
        contentTypesFound
        contentTypesMissing
        coveragePct
        feedback
      }
    }
  }
`;

export const REGENERATE_BEAT_SUMMARY = `
  mutation RegenerateBeatSummary($plotBeatId: ID!) {
    regenerateBeatSummary(input: { plotBeatId: $plotBeatId }) {
      success
      errors
      summary
    }
  }
`;

export const ADVANCE_DEEP_DIVE_PHASE = `
  mutation AdvanceDeepDivePhase($draftBlockId: ID!, $targetPhase: String!) {
    advanceDeepDivePhase(input: { draftBlockId: $draftBlockId, targetPhase: $targetPhase }) {
      success
      errors
      draftBlock {
        id
        deepDivePhase
        interviewReady
      }
      backgroundJob {
        id
        status
        progress
      }
    }
  }
`;

// AI Output Evaluations
export const GET_AI_EVALUATIONS = `
  query AiEvaluations($requestType: String, $bookId: ID, $minScore: Float, $maxScore: Float, $limit: Int, $offset: Int) {
    aiOutputEvaluations(requestType: $requestType, bookId: $bookId, minScore: $minScore, maxScore: $maxScore, limit: $limit, offset: $offset) {
      id
      aiPromptLogId
      coherenceScore
      engagementScore
      relevanceScore
      proseQualityScore
      depthScore
      repetitivenessScore
      overallScore
      outputWordCount
      evaluationModel
      evaluationDurationMs
      evaluationNotes
      errorMessage
      success
      requestType
      bookTitle
      modelUsed
      logCreatedAt
      createdAt
    }
    aiEvaluationsStats(requestType: $requestType, bookId: $bookId) {
      total
      avgOverallScore
      avgCoherenceScore
      avgEngagementScore
      avgRelevanceScore
      avgProseQualityScore
      avgDepthScore
      avgRepetitivenessScore
      failedCount
    }
    booksWithLogs {
      id
      title
    }
  }
`;

export const GET_AI_EVALUATION_DETAIL = `
  query AiEvaluationDetail($id: ID!) {
    aiOutputEvaluation(id: $id) {
      id
      aiPromptLogId
      coherenceScore
      engagementScore
      relevanceScore
      proseQualityScore
      depthScore
      repetitivenessScore
      overallScore
      outputWordCount
      evaluationModel
      evaluationDurationMs
      evaluationNotes
      criteriaDetails
      errorMessage
      success
      requestType
      bookTitle
      modelUsed
      logCreatedAt
      outputResponse
      createdAt
    }
  }
`;

export const UPDATE_MAIN_IDEAS = `
  mutation UpdateMainIdeas($draftBlockId: ID!, $mainIdeas: [String!]!) {
    updateMainIdeas(input: { draftBlockId: $draftBlockId, mainIdeas: $mainIdeas }) {
      success
      errors
    }
  }
`;

// Chapters-first redesign — Phase 2/4 chapter & scene flow
export const GENERATE_CHAPTER_FLOW_OPTIONS = `
  mutation GenerateChapterFlowOptions($bookId: ID!, $minChapters: Int, $maxChapters: Int, $instructions: String) {
    generateChapterFlowOptions(input: { bookId: $bookId, minChapters: $minChapters, maxChapters: $maxChapters, instructions: $instructions }) {
      success
      errors
      options {
        id
        label
        summary
        chapters {
          title
          description
          covers
        }
      }
    }
  }
`;

export const RESET_BOOK_STRUCTURE = `
  mutation ResetBookStructure($bookId: ID!) {
    resetBookStructure(input: { bookId: $bookId }) {
      success
      errors
    }
  }
`;

export const GENERATE_CHAPTER_COVERS = `
  mutation GenerateChapterCovers($bookId: ID!, $chapterTitle: String!, $instructions: String, $siblings: [String!]) {
    generateChapterCovers(input: { bookId: $bookId, chapterTitle: $chapterTitle, instructions: $instructions, siblings: $siblings }) {
      success
      errors
      covers
    }
  }
`;

export const APPLY_CHAPTER_FLOW = `
  mutation ApplyChapterFlow($bookId: ID!, $chapters: [ChapterFlowChapterInput!]!) {
    applyChapterFlow(input: { bookId: $bookId, chapters: $chapters }) {
      success
      errors
      book {
        id
        chapters {
          id
          title
          description
          position
          scenes {
            id
            title
            position
          }
        }
      }
    }
  }
`;

export const GENERATE_BOOK_PREVIEW = `
  mutation GenerateBookPreview($bookId: ID!) {
    generateBookPreview(input: { bookId: $bookId }) {
      success
      errors
    }
  }
`;

export const GENERATE_SCENE_PREVIEW = `
  mutation GenerateScenePreview($sceneId: ID!, $force: Boolean) {
    generateScenePreview(input: { sceneId: $sceneId, force: $force }) {
      success
      errors
      scene {
        id
        body
        previewContent
        locked
        previewStale
      }
    }
  }
`;

export const GENERATE_SCENE_PLAN = `
  mutation GenerateScenePlan($sceneId: ID!, $force: Boolean) {
    generateScenePlan(input: { sceneId: $sceneId, force: $force }) {
      success
      errors
      scene {
        id
        planPointCount
      }
    }
  }
`;

export const LOCK_SCENE = `
  mutation LockScene($sceneId: ID!) {
    lockScene(input: { sceneId: $sceneId }) {
      success
      errors
      scene { id locked lockedAt }
    }
  }
`;

export const UNLOCK_SCENE = `
  mutation UnlockScene($sceneId: ID!) {
    unlockScene(input: { sceneId: $sceneId }) {
      success
      errors
      scene { id locked lockedAt }
    }
  }
`;

export const GET_BOOK_CHAPTERS_TREE = `
  query GetBookChaptersTree($id: ID!) {
    book(id: $id) {
      id
      title
      fullTitle
      language
      expansionStatus
      chapters {
        id
        bookId
        title
        description
        position
        wordCount
        scenes {
          id
          chapterId
          title
          position
          body
          previewContent
          wordCount
          health
          locked
          previewStale
          lockedAt
          previewStaleAt
          planPoints {
            id
            content
            position
            locked
            sourceType
            bookPromiseId
          }
          contextItems {
            id
            bookId
            contextableType
            contextableId
            kind
            content
            position
            createdAt
            updatedAt
          }
          updatedAt
        }
      }
      bookPromises {
        id
        bookId
        content
        position
        sourceType
        externalKey
        addressed
      }
      bookGuides {
        id
        bookId
        slug
        title
        content
        includeInGeneration
        scope
        externalKey
      }
      bookFacts {
        id
        bookId
        claim
        source
        status
        includeInGeneration
        position
        externalKey
      }
    }
  }
`;

// --- Book guides (free-form book-level guidance injected into generation) ---
const BOOK_GUIDE_FIELDS = `
  id
  bookId
  slug
  title
  content
  includeInGeneration
  scope
  externalKey
`;

export const SAVE_BOOK_GUIDE = `
  mutation SaveBookGuide($bookId: ID!, $bookGuideId: ID, $slug: String, $title: String, $content: String, $scope: String, $includeInGeneration: Boolean) {
    saveBookGuide(input: {
      bookId: $bookId
      bookGuideId: $bookGuideId
      slug: $slug
      title: $title
      content: $content
      scope: $scope
      includeInGeneration: $includeInGeneration
    }) {
      bookGuide { ${BOOK_GUIDE_FIELDS} }
      errors
    }
  }
`;

export const DELETE_BOOK_GUIDE = `
  mutation DeleteBookGuide($bookGuideId: ID!) {
    deleteBookGuide(input: { bookGuideId: $bookGuideId }) {
      deletedId
      errors
    }
  }
`;

// --- Book facts (trusted real-world claims for grounding + fact-checking) ---
const BOOK_FACT_FIELDS = `
  id
  bookId
  claim
  source
  status
  includeInGeneration
  position
  externalKey
`;

export const SAVE_BOOK_FACT = `
  mutation SaveBookFact($bookId: ID!, $bookFactId: ID, $claim: String!, $source: String, $status: String, $includeInGeneration: Boolean, $position: Int) {
    saveBookFact(input: {
      bookId: $bookId
      bookFactId: $bookFactId
      claim: $claim
      source: $source
      status: $status
      includeInGeneration: $includeInGeneration
      position: $position
    }) {
      bookFact { ${BOOK_FACT_FIELDS} }
      errors
    }
  }
`;

export const DELETE_BOOK_FACT = `
  mutation DeleteBookFact($bookFactId: ID!) {
    deleteBookFact(input: { bookFactId: $bookFactId }) {
      deletedId
      errors
    }
  }
`;

// Fact-check a scene's prose against the book's facts (flags only; no edits).
export const CHECK_SCENE_FACTS = `
  mutation CheckSceneFacts($sceneId: ID!) {
    checkSceneFacts(input: { sceneId: $sceneId }) {
      findings {
        claim
        verdict
        sentence
        factKey
        reason
      }
      errors
    }
  }
`;

// --- Book promises (book-level essentials, the layer above chapters) ---
const BOOK_PROMISE_FIELDS = `
  id
  bookId
  content
  position
  sourceType
  externalKey
  addressed
`;

export const SAVE_BOOK_PROMISE = `
  mutation SaveBookPromise($bookId: ID!, $bookPromiseId: ID, $content: String!, $position: Int) {
    saveBookPromise(input: {
      bookId: $bookId
      bookPromiseId: $bookPromiseId
      content: $content
      position: $position
    }) {
      bookPromise { ${BOOK_PROMISE_FIELDS} }
      errors
    }
  }
`;

export const DELETE_BOOK_PROMISE = `
  mutation DeleteBookPromise($bookPromiseId: ID!) {
    deleteBookPromise(input: { bookPromiseId: $bookPromiseId }) {
      deletedId
      errors
    }
  }
`;

export const SET_SCENE_PLAN_POINT_PROMISE = `
  mutation SetScenePlanPointPromise($planPointId: ID!, $bookPromiseId: ID) {
    setScenePlanPointPromise(input: { planPointId: $planPointId, bookPromiseId: $bookPromiseId }) {
      planPoint { id bookPromiseId }
      errors
    }
  }
`;

export const REORDER_BOOK_PROMISES = `
  mutation ReorderBookPromises($bookId: ID!, $bookPromiseIds: [ID!]!) {
    reorderBookPromises(input: { bookId: $bookId, bookPromiseIds: $bookPromiseIds }) {
      success
      errors
    }
  }
`;

export const GENERATE_BOOK_PROMISE_SUGGESTIONS = `
  mutation GenerateBookPromiseSuggestions($bookId: ID!, $count: Int) {
    generateBookPromiseSuggestions(input: { bookId: $bookId, count: $count }) {
      success
      suggestions
      errors
    }
  }
`;

// Book creation reading mode: chapters → scenes with the prose the reader shows,
// plus the autogenerate flag and the current user's reading position.
export const GET_BOOK_READER = `
  query GetBookReader($id: ID!) {
    book(id: $id) {
      id
      title
      fullTitle
      autoGenerate
      githubSyncConfigured
      readingPosition {
        blockId
        charOffset
      }
      chapters {
        id
        title
        position
        scenes {
          id
          title
          position
          body
          previewContent
          health
          wordCount
          planPoints {
            id
            content
          }
        }
      }
    }
  }
`;

export const SET_BOOK_AUTO_GENERATE = `
  mutation SetBookAutoGenerate($bookId: ID!, $enabled: Boolean!) {
    setBookAutoGenerate(input: { bookId: $bookId, enabled: $enabled }) {
      success
      errors
      book {
        id
        autoGenerate
      }
    }
  }
`;

export const SAVE_BOOK_READING_POSITION = `
  mutation SaveBookReadingPosition($bookId: ID!, $blockId: String!, $charOffset: Int) {
    saveBookReadingPosition(input: { bookId: $bookId, blockId: $blockId, charOffset: $charOffset }) {
      success
      errors
    }
  }
`;

export const SAVE_CONTEXT_ITEM = `
  mutation SaveContextItem(
    $contextItemId: ID
    $contextableType: String
    $contextableId: ID
    $kind: String
    $content: String
    $position: Int
  ) {
    saveContextItem(input: {
      contextItemId: $contextItemId
      contextableType: $contextableType
      contextableId: $contextableId
      kind: $kind
      content: $content
      position: $position
    }) {
      contextItem {
        id
        bookId
        contextableType
        contextableId
        kind
        content
        position
        createdAt
        updatedAt
      }
      errors
    }
  }
`;

export const DELETE_CONTEXT_ITEM = `
  mutation DeleteContextItem($contextItemId: ID!) {
    deleteContextItem(input: { contextItemId: $contextItemId }) {
      success
      errors
    }
  }
`;

export const REORDER_CHAPTERS = `
  mutation ReorderChapters($bookId: ID!, $chapterIds: [ID!]!) {
    reorderChapters(input: { bookId: $bookId, chapterIds: $chapterIds }) {
      success
      errors
    }
  }
`;

export const REORDER_SCENES = `
  mutation ReorderScenes($chapterId: ID!, $sceneIds: [ID!]!) {
    reorderScenes(input: { chapterId: $chapterId, sceneIds: $sceneIds }) {
      success
      errors
    }
  }
`;

export const CREATE_CHAPTER = `
  mutation CreateChapter($bookId: ID!, $title: String!, $description: String) {
    createChapter(input: { bookId: $bookId, title: $title, description: $description }) {
      success
      errors
      chapter { id title position }
    }
  }
`;

export const CREATE_SCENE = `
  mutation CreateScene($chapterId: ID!, $title: String!) {
    createScene(input: { chapterId: $chapterId, title: $title }) {
      success
      errors
      scene { id title position }
    }
  }
`;

export const UPDATE_CHAPTER = `
  mutation UpdateChapter($chapterId: ID!, $title: String, $description: String) {
    updateChapter(input: { chapterId: $chapterId, title: $title, description: $description }) {
      success
      errors
      chapter {
        id
        title
        description
        updatedAt
      }
    }
  }
`;

export const UPDATE_SCENE = `
  mutation UpdateScene($sceneId: ID!, $title: String) {
    updateScene(input: { sceneId: $sceneId, title: $title }) {
      success
      errors
      scene {
        id
        title
        updatedAt
      }
    }
  }
`;

export const DELETE_CHAPTER = `
  mutation DeleteChapter($chapterId: ID!) {
    deleteChapter(input: { chapterId: $chapterId }) {
      success
      errors
    }
  }
`;

export const DELETE_SCENE = `
  mutation DeleteScene($sceneId: ID!) {
    deleteScene(input: { sceneId: $sceneId }) {
      success
      errors
    }
  }
`;

export const GENERATE_SCENE_FLOW_OPTIONS = `
  mutation GenerateSceneFlowOptions($chapterId: ID!) {
    generateSceneFlowOptions(input: { chapterId: $chapterId }) {
      success
      errors
      options {
        id
        label
        summary
        scenes {
          title
          description
          covers
        }
      }
    }
  }
`;

export const GET_SCENE_REVISIONS = `
  query GetSceneRevisions($id: ID!) {
    scene(id: $id) {
      id
      title
      textRevisions {
        id
        version
        contentKind
        origin
        content
        createdAt
      }
    }
  }
`;

export const GET_SCENE_EDITOR = `
  query GetSceneEditor($sceneId: ID!) {
    scene(id: $sceneId) {
      id
      chapterId
      title
      position
      body
      previewContent
      fullTextContent
      wordCount
      health
      locked
      previewStale
      updatedAt
      contextItems {
        id
        bookId
        contextableType
        contextableId
        kind
        content
        position
        createdAt
        updatedAt
      }
      planPoints {
        id
        sceneId
        content
        position
        locked
        sourceType
        createdAt
        updatedAt
      }
      sceneInterviews {
        id
        sceneId
        title
        position
        status
        messages {
          id
          sceneInterviewId
          scenePlanPointId
          proposedAngles
          role
          content
          position
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      storyBiblePlotBeat {
        id
        title
        draftBlock {
          id
          content
          writingStyle
          writingStyleSuggestions
        }
      }
      updatedAt
    }
  }
`;

export const UPDATE_SCENE_BODY = `
  mutation UpdateSceneBody($sceneId: ID!, $content: String!) {
    updateSceneBody(input: { sceneId: $sceneId, content: $content }) {
      success
      errors
      scene {
        id
        body
        previewContent
        fullTextContent
        wordCount
        health
        updatedAt
      }
    }
  }
`;

export const SUGGEST_SCENE_WORDING = `
  mutation SuggestSceneWording($sceneId: ID!, $selectedText: String!, $precedingText: String, $followingText: String) {
    suggestSceneWording(
      input: {
        sceneId: $sceneId
        selectedText: $selectedText
        precedingText: $precedingText
        followingText: $followingText
      }
    ) {
      suggestions
      errors
    }
  }
`;

export const GENERATE_SCENE_PLAN_POINT_SUGGESTIONS = `
  mutation GenerateScenePlanPointSuggestions($sceneId: ID!, $guidance: String, $focusTag: String, $mode: String) {
    generateScenePlanPointSuggestions(input: { sceneId: $sceneId, guidance: $guidance, focusTag: $focusTag, mode: $mode }) {
      errors
      suggestions {
        items {
          content
          rationale
          tag
        }
      }
    }
  }
`;

export const SAVE_SCENE_PLAN_POINT = `
  mutation SaveScenePlanPoint($sceneId: ID!, $planPointId: ID, $content: String!, $position: Int) {
    saveScenePlanPoint(input: {
      sceneId: $sceneId
      planPointId: $planPointId
      content: $content
      position: $position
    }) {
      planPoint {
        id
        sceneId
        content
        position
        locked
        sourceType
      }
      errors
    }
  }
`;

export const REORDER_SCENE_PLAN_POINTS = `
  mutation ReorderScenePlanPoints($sceneId: ID!, $planPointIds: [ID!]!) {
    reorderScenePlanPoints(input: { sceneId: $sceneId, planPointIds: $planPointIds }) {
      success
      errors
    }
  }
`;

export const DELETE_SCENE_PLAN_POINT = `
  mutation DeleteScenePlanPoint($planPointId: ID!) {
    deleteScenePlanPoint(input: { planPointId: $planPointId }) {
      success
      errors
    }
  }
`;

export const LOCK_SCENE_PLAN_POINT = `
  mutation LockScenePlanPoint($planPointId: ID!) {
    lockScenePlanPoint(input: { planPointId: $planPointId }) {
      success
      errors
      planPoint {
        id
        locked
      }
    }
  }
`;

export const UNLOCK_SCENE_PLAN_POINT = `
  mutation UnlockScenePlanPoint($planPointId: ID!) {
    unlockScenePlanPoint(input: { planPointId: $planPointId }) {
      success
      errors
      planPoint {
        id
        locked
      }
    }
  }
`;

export const START_SCENE_INTERVIEW = `
  mutation StartSceneInterview($sceneId: ID!, $title: String, $firstQuestion: String) {
    startSceneInterview(input: {
      sceneId: $sceneId
      title: $title
      firstQuestion: $firstQuestion
    }) {
      success
      errors
      interview {
        id
        sceneId
        title
        status
        messages {
          id
          scenePlanPointId
          proposedAngles
          role
          content
          position
          createdAt
        }
      }
      message {
        id
        scenePlanPointId
        proposedAngles
        role
        content
        position
        createdAt
      }
    }
  }
`;

export const SEND_SCENE_INTERVIEW_MESSAGE = `
  mutation SendSceneInterviewMessage($interviewId: ID!, $content: String!) {
    sendSceneInterviewMessage(input: { interviewId: $interviewId, content: $content }) {
      success
      errors
      interview {
        id
        status
        messages {
          id
          scenePlanPointId
          proposedAngles
          role
          content
          position
          createdAt
        }
      }
      message {
        id
        scenePlanPointId
        proposedAngles
        role
        content
        position
        createdAt
      }
    }
  }
`;

export const UPDATE_SCENE_INTERVIEW_MESSAGE = `
  mutation UpdateSceneInterviewMessage($messageId: ID!, $content: String!) {
    updateSceneInterviewMessage(input: { messageId: $messageId, content: $content }) {
      success
      errors
      message {
        id
        scenePlanPointId
        proposedAngles
        role
        content
        position
        createdAt
      }
    }
  }
`;

export const CLEAR_SCENE_INTERVIEW = `
  mutation ClearSceneInterview($interviewId: ID!) {
    clearSceneInterview(input: { interviewId: $interviewId }) {
      success
      errors
    }
  }
`;

export const SWITCH_SCENE_INTERVIEW_ANGLE = `
  mutation SwitchSceneInterviewAngle($interviewId: ID!, $scenePlanPointId: ID!) {
    switchSceneInterviewAngle(input: { interviewId: $interviewId, scenePlanPointId: $scenePlanPointId }) {
      success
      errors
      interview {
        id
        status
        messages {
          id
          scenePlanPointId
          proposedAngles
          role
          content
          position
          createdAt
        }
      }
      message {
        id
        scenePlanPointId
        proposedAngles
        role
        content
        position
        createdAt
      }
    }
  }
`;

export const APPLY_SCENE_FLOW = `
  mutation ApplySceneFlow($chapterId: ID!, $scenes: [SceneFlowSceneInput!]!) {
    applySceneFlow(input: { chapterId: $chapterId, scenes: $scenes }) {
      success
      errors
      chapter {
        id
        scenes {
          id
          chapterId
          title
          position
          body
          previewContent
          locked
          previewStale
        }
      }
    }
  }
`;

export const GET_BOOK_SETUP_STATUS = `
  query GetBookSetupStatus($id: ID!) {
    book(id: $id) {
      id
      title
      summary
      writingStyle
      coverImageUrl
      readerAgeFrom
      readerAgeTo
      audienceReviewed
      githubSyncConfigured
    }
  }
`;

export const GET_BOOK_GITHUB_SYNC = `
  query GetBookGithubSync($id: ID!) {
    book(id: $id) {
      id
      title
      githubRepo
      githubSyncConfigured
      lastSyncedCommitSha
      lastSyncedAt
      anySyncConflicts
      writingStyle
      writingStyleConflicted
      writingStyleConflictRepoText
      conflictedScenes {
        id
        title
        body
        conflictRepoText
      }
    }
  }
`;

export const GET_BOOK_GITHUB_SYNC_STATE = `
  query GetBookGithubSyncState($bookId: ID!) {
    bookGithubSyncState(bookId: $bookId) {
      syncConfigured
      remoteCommitSha
      lastSyncedCommitSha
      remoteAhead
      incomingCount
      conflictCount
      reorderCount
    }
  }
`;

export const CONFIGURE_GITHUB_SYNC = `
  mutation ConfigureGithubSync($bookId: ID!, $githubRepo: String!) {
    configureGithubSync(input: { bookId: $bookId, githubRepo: $githubRepo }) {
      book {
        id
        githubRepo
        githubSyncConfigured
        lastSyncedCommitSha
        lastSyncedAt
      }
      errors
    }
  }
`;

export const GET_GITHUB_CONNECTION = `
  query GetGithubConnection {
    me {
      id
      githubConnected
      githubLogin
    }
  }
`;

export const START_GITHUB_OAUTH = `
  mutation StartGithubOauth($returnTo: String) {
    startGithubOauth(input: { returnTo: $returnTo }) {
      authorizeUrl
      errors
    }
  }
`;

export const DISCONNECT_GITHUB = `
  mutation DisconnectGithub {
    disconnectGithub(input: {}) {
      user {
        id
        githubConnected
        githubLogin
      }
      errors
    }
  }
`;

export const GET_GITHUB_REPOS = `
  query GetGithubRepos {
    githubRepos {
      fullName
      name
      private
      defaultBranch
      description
      pushedAt
      ownerLogin
      ownerType
    }
  }
`;

export const PULL_BOOK_FROM_GITHUB = `
  mutation PullBookFromGithub($bookId: ID!) {
    pullBookFromGithub(input: { bookId: $bookId }) {
      summary {
        updated
        created
        tombstoned
        conflicted
      }
      errors
    }
  }
`;

export const RESOLVE_SCENE_CONFLICT = `
  mutation ResolveSceneConflict($sceneId: ID!, $choice: String!, $mergedText: String) {
    resolveSceneConflict(input: { sceneId: $sceneId, choice: $choice, mergedText: $mergedText }) {
      scene {
        id
        conflicted
      }
      errors
    }
  }
`;

export const RESOLVE_WRITING_STYLE_CONFLICT = `
  mutation ResolveWritingStyleConflict($bookId: ID!, $choice: String!, $mergedText: String) {
    resolveWritingStyleConflict(input: { bookId: $bookId, choice: $choice, mergedText: $mergedText }) {
      book {
        id
        writingStyleConflicted
      }
      errors
    }
  }
`;

export const FORK_BOOK_FROM_GITHUB = `
  mutation ForkBookFromGithub($bookId: ID!) {
    forkBookFromGithub(input: { bookId: $bookId }) {
      book {
        id
        title
      }
      errors
    }
  }
`;

export const PUSH_BOOK_TO_GITHUB = `
  mutation PushBookToGithub($bookId: ID!, $message: String) {
    pushBookToGithub(input: { bookId: $bookId, message: $message }) {
      book {
        id
        lastSyncedCommitSha
        lastSyncedAt
      }
      status {
        committed
        noChanges
        commitSha
        branch
      }
      conflicts
      errors
    }
  }
`;

// ---------------------------------------------------------------------------
// Book Chat Companion
// ---------------------------------------------------------------------------

const COMPANION_PROPOSAL_FIELDS = `
  id
  kind
  status
  title
  rationale
  impact
  structural
  revertable
  createdAt
  appliedAt
  revertedAt
`;

export const GET_BOOK_COMPANION_CHAT = `
  query BookCompanionChat($bookId: ID!, $focus: String) {
    bookCompanionChat(bookId: $bookId, focus: $focus) {
      id
      createdAt
      messages {
        id
        role
        content
        createdAt
        proposals { ${COMPANION_PROPOSAL_FIELDS} }
      }
      pendingProposals { ${COMPANION_PROPOSAL_FIELDS} }
      appliedProposals { ${COMPANION_PROPOSAL_FIELDS} }
    }
  }
`;

export const SEND_COMPANION_MESSAGE = `
  mutation SendCompanionMessage($bookId: ID!, $content: String!, $focus: String) {
    sendCompanionMessage(input: { bookId: $bookId, content: $content, focus: $focus }) {
      success
      errors
      message {
        id
        role
        content
        createdAt
        proposals { ${COMPANION_PROPOSAL_FIELDS} }
      }
      proposals { ${COMPANION_PROPOSAL_FIELDS} }
    }
  }
`;

export const APPLY_COMPANION_PROPOSAL = `
  mutation ApplyCompanionProposal($proposalId: ID!) {
    applyCompanionProposal(input: { proposalId: $proposalId }) {
      success
      errors
      proposal { ${COMPANION_PROPOSAL_FIELDS} }
    }
  }
`;

export const DISMISS_COMPANION_PROPOSAL = `
  mutation DismissCompanionProposal($proposalId: ID!) {
    dismissCompanionProposal(input: { proposalId: $proposalId }) {
      success
      errors
      proposal { ${COMPANION_PROPOSAL_FIELDS} }
    }
  }
`;

export const REVERT_COMPANION_PROPOSAL = `
  mutation RevertCompanionProposal($proposalId: ID!) {
    revertCompanionProposal(input: { proposalId: $proposalId }) {
      success
      errors
      proposal { ${COMPANION_PROPOSAL_FIELDS} }
    }
  }
`;

export const START_COMPANION_CHAT = `
  mutation StartCompanionChat($bookId: ID!, $focus: String) {
    startCompanionChat(input: { bookId: $bookId, focus: $focus }) {
      success
      errors
      chat { id createdAt }
    }
  }
`;
